/**
 * VaultFileDetails: Slide-in panel showing file metadata and actions
 */
import { useState } from 'react';
import {
  X,
  Download,
  Pencil,
  Trash2,
  Move,
  Copy,
  FileText,
  Image,
  FileSpreadsheet,
  Archive,
  Video,
  Music,
  File,
  Ruler,
} from 'lucide-react';
import type { VaultFile } from '../../types/vault.types';
import { formatFileSize, getFileCategory } from '../../types/vault.types';

interface VaultFileDetailsProps {
  file: VaultFile;
  onClose: () => void;
  onDownload: (storagePath: string) => Promise<void>;
  onRename: (fileId: string, newName: string) => Promise<void>;
  onDelete: (fileId: string) => void | Promise<void>;
  onMove: (fileId: string) => void;
  onCopy: (fileId: string) => void;
  previewUrl?: string | null;
}

export function VaultFileDetails({
  file,
  onClose,
  onDownload,
  onRename,
  onDelete,
  onMove,
  onCopy,
  previewUrl,
}: VaultFileDetailsProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(file.name);
  const category = getFileCategory(file.mime_type, file.name);


  const handleRename = async () => {
    if (editName.trim() && editName.trim() !== file.name) {
      await onRename(file.id, editName);
    }
    setIsEditing(false);
  };

  const getCategoryIcon = () => {
    const size = 48;
    switch (category) {
      case 'image': return <Image size={size} />;
      case 'pdf': return <FileText size={size} />;
      case 'document': return <FileText size={size} />;
      case 'spreadsheet': return <FileSpreadsheet size={size} />;
      case 'archive': return <Archive size={size} />;
      case 'video': return <Video size={size} />;
      case 'audio': return <Music size={size} />;
      case 'cad': return <Ruler size={size} />;
      default: return <File size={size} />;
    }
  };

  const ext = file.name.split('.').pop()?.toUpperCase() || 'FILE';

  return (
    <div className="vault-details-panel">
      <div className="vault-details-header">
        <h3>File Details</h3>
        <button className="vault-details-close" onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      {/* Preview area */}
      <div className="vault-details-preview">
        {category === 'image' && previewUrl ? (
          <img src={previewUrl} alt={file.name} className="vault-details-image" />
        ) : (
          <div className="vault-details-icon-preview">
            {getCategoryIcon()}
            <span className="vault-details-ext">.{ext}</span>
          </div>
        )}
      </div>

      {/* File name */}
      <div className="vault-details-name-section">
        {isEditing ? (
          <div className="vault-details-name-edit">
            <input
              className="vault-inline-input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename();
                if (e.key === 'Escape') setIsEditing(false);
              }}
              onBlur={handleRename}
              autoFocus
            />
          </div>
        ) : (
          <h4 className="vault-details-filename">{file.name}</h4>
        )}
      </div>

      {/* Metadata */}
      <div className="vault-details-meta">
        <div className="vault-details-meta-row">
          <span className="vault-details-meta-label">Size</span>
          <span className="vault-details-meta-value">{formatFileSize(file.size_bytes)}</span>
        </div>
        <div className="vault-details-meta-row">
          <span className="vault-details-meta-label">Type</span>
          <span className="vault-details-meta-value">{file.mime_type || ext}</span>
        </div>
        <div className="vault-details-meta-row">
          <span className="vault-details-meta-label">Uploaded</span>
          <span className="vault-details-meta-value">
            {new Date(file.created_at).toLocaleDateString('en-AU', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
        {file.updated_at !== file.created_at && (
          <div className="vault-details-meta-row">
            <span className="vault-details-meta-label">Modified</span>
            <span className="vault-details-meta-value">
              {new Date(file.updated_at).toLocaleDateString('en-AU', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="vault-details-actions">
        <button className="vault-action-btn primary" onClick={() => onDownload(file.storage_path)}>
          <Download size={16} /> Download
        </button>
        <button className="vault-action-btn" onClick={() => { setIsEditing(true); setEditName(file.name); }}>
          <Pencil size={16} /> Rename
        </button>
        <button className="vault-action-btn" onClick={() => onMove(file.id)}>
          <Move size={16} /> Move
        </button>
        <button className="vault-action-btn" onClick={() => onCopy(file.id)}>
          <Copy size={16} /> Copy
        </button>
        <button
          className="vault-action-btn danger"
          onClick={() => onDelete(file.id)}
        >
          <Trash2 size={16} /> Delete
        </button>
      </div>
    </div>
  );
}
